import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { langBySlug } from "../lib/editions";
import { ProductCard } from "../components/shop/ProductCard";
import { ShopFilters } from "../components/shop/ShopFilters";
import { Flag } from "../components/shop/Flag";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const KEYS = ["category", "language", "set", "condition", "availability_status", "min_price", "max_price", "q"];

export default function Shop() {
  const { t, tr, lang } = useLang();
  const { lang: slug } = useParams();
  const edition = langBySlug(slug);
  const [params, setParams] = useSearchParams();
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""]));
  if (edition && !params.has("language")) f.language = edition.code;
  const [sort, setSort] = useState("newest");
  const [products, setProducts] = useState(null);
  const [facets, setFacets] = useState(null);
  const [mobile, setMobile] = useState(false);
  const key = JSON.stringify(f);

  useEffect(() => {
    const q = JSON.parse(key);
    api.get("/products-facets", { params: Object.fromEntries(Object.entries(q).filter(([k, v]) => v && k !== "q")) }).then((r) => setFacets(r.data));
    api.get("/products", { params: { ...Object.fromEntries(Object.entries(q).filter(([, v]) => v)), sort } }).then((r) => setProducts(r.data));
  }, [key, sort]);

  const nav = useNavigate();
  const setF = (k, v) => {
    const upd = typeof k === "string" ? { [k]: v } : k;
    const n = new URLSearchParams(params);
    Object.entries(upd).forEach(([key, val]) => (val ? n.set(key, val) : n.delete(key)));
    if (edition && "language" in upd) { nav(`/shop?${n.toString()}`); return; }
    if (edition && !n.has("language")) n.set("language", edition.code);
    setParams(n);
  };
  const active = KEYS.filter((k) => f[k] && !(edition && k === "language" && f[k] === edition.code)).length;
  const title = edition ? `Pokémon TCG – ${lang === "en" ? edition.en : edition.de}` : f.category && !f.category.includes(",") ? t("cat")[f.category] : f.q ? `„${f.q}“` : t("all");

  return (
    <div className="bg-[#F7F5FB] min-h-screen">
      <div className="bg-[#120E1F] grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left">
          {edition && <Flag code={edition.code} className="h-10 w-[60px] sm:h-12 sm:w-[72px]" />}
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#F2C230]">Jolgen Cards Shop</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-3" data-testid="shop-title">{title}</h1>
            {edition && <p className="text-white/60 mt-2 text-sm" data-testid="edition-tagline">{lang === "en" ? edition.tagEn : edition.tagDe}</p>}
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden lg:block bg-white rounded-2xl border border-[#120E1F]/5 p-5 h-fit sticky top-36"><ShopFilters f={f} setF={setF} facets={facets} /></aside>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-3">
              <div className="lg:hidden"><button onClick={() => setMobile(true)} className="btn-outline" data-testid="mobile-filters-btn"><SlidersHorizontal className="h-4 w-4" />{tr("Filter", "Filters")}{active ? ` (${active})` : ""}</button></div>
              <span className="text-xs text-[#707783]" data-testid="results-count">{products ? t("results", { n: products.length }) : ""}</span>
              {active > 0 && <button onClick={() => setParams(new URLSearchParams())} className="text-xs text-[#6E3FB8] underline inline-flex items-center gap-1" data-testid="clear-filters-btn"><X className="h-3 w-3" />{tr("Filter zurücksetzen", "Clear filters")}</button>}
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-48 bg-white rounded-full" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>{["newest", "price_asc", "price_desc", "stock"].map((s) => <SelectItem key={s} value={s} data-testid={`sort-${s}`}>{t(`sort_${s}`)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {products && products.length === 0 && <p className="text-center py-24 text-[#707783]" data-testid="no-results">{t("no_results")}</p>}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-testid="product-grid">{(products || []).map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</div>
        </div>
      </div>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="left" className="w-[88%] max-w-sm overflow-y-auto bg-white" data-testid="mobile-filters-sheet">
          <SheetHeader><SheetTitle className="font-display text-2xl">{tr("Filter", "Filters")}</SheetTitle><SheetDescription className="sr-only">filters</SheetDescription></SheetHeader>
          <div className="mt-4"><ShopFilters f={f} setF={setF} facets={facets} /></div>
          <button onClick={() => setMobile(false)} className="btn-gold w-full mt-6" data-testid="mobile-filters-apply">{products ? t("results", { n: products.length }) : "OK"}</button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
